import { ESessionType } from '@mindverse/accessor-open/dist/interface/session';
import classnames from 'classnames';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import type { ReactClassNameInterface } from '@/types/common';

import useMindStorage, { EStorageKey } from '../../hooks/useMindStorage';
import StopMsg from '../../services/stop';
import { CONTAINER_EVENT } from '../../utils/utils';
import styles from './index.less';

interface IProps extends ReactClassNameInterface {
  inChatMode: boolean;
}

function Stop({ className, inChatMode }: IProps): JSX.Element | null {
  const [hidden, setHidden] = useState<boolean>(true);
  const [canShow, setCanShow] = useState<boolean>(false);
  const [answering] = useMindStorage(EStorageKey.ANSWER_ING);
  const [messageId] = useMindStorage(EStorageKey.MESSAGE_ID);
  const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);
  const [sessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);
  const [, setAnswerStopped] = useMindStorage(EStorageKey.ANSWER_STOPPED);
  const timer1 = useRef<ReturnType<typeof setTimeout>>();
  const timer2 = useRef<ReturnType<typeof setTimeout>>();

  const handleStop = useCallback(() => {
    setAnswerStopped(true);

    if (messageId && sessionId) {
      StopMsg({ messageId, sessionId });
    }

    // 立即切换样式
    setHidden(true);

    // 动画后销毁组件
    setTimeout(() => {
      setCanShow(false);
    }, 100);
  }, [setAnswerStopped, messageId, sessionId]);

  useEffect(() => {
    if (
      sessionInfo?.type &&
      ![ESessionType.GROUP, ESessionType.OTHERBOT, ESessionType.FRIEND].includes(sessionInfo.type)
    ) {
      setCanShow(false);

      return;
    }

    if (answering) {
      setCanShow(true);

      timer1.current = setTimeout(() => {
        setHidden(false);
      }, 100);
    } else {
      setHidden(true);

      timer2.current = setTimeout(() => {
        setCanShow(false);
      }, 100);
    }

    return () => {
      if (answering) {
        clearTimeout(timer1.current);
      } else {
        clearTimeout(timer2.current);
      }
    };
  }, [answering, sessionInfo]);

  useEffect(() => {
    addEventListener(CONTAINER_EVENT.STOP_ANSWER, handleStop);

    return () => {
      removeEventListener(CONTAINER_EVENT.STOP_ANSWER, handleStop);
    };
  }, [handleStop]);

  if (!canShow) {
    return null;
  }

  return (
    <div
      className={classnames(styles.stop, className, {
        [styles.hidden]: hidden,
        [styles.noChatMode]: !inChatMode
      })}
      onClick={handleStop}
    >
      <span />
      Stop
    </div>
  );
}

export default memo(Stop);
